import type { FC } from 'react';

import { Button, Icon, Stack, Tag, Text } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

/**
 * The section header's bulk-review affordances: "Confirm all N" while rows are
 * pending, an "All confirmed" pill once every reviewable row is approved or
 * edited, and an Undo for the last bulk confirmation instead of silently
 * removing the control.
 */
export const SectionBulkControls: FC<{
  title: string;
  confirmableCount: number;
  allConfirmed: boolean;
  showUndoAll: boolean;
  disabled?: boolean;
  onConfirmAll: () => void;
  onUndoAll?: () => void;
}> = ({
  title,
  confirmableCount,
  allConfirmed,
  showUndoAll,
  disabled,
  onConfirmAll,
  onUndoAll,
}) => {
  const { t } = useTranslation();
  return (
    <Stack direction="horizontal" align="center" justify="between" gap={3}>
      <Text weight="semibold">{title}</Text>
      <Stack
        direction="horizontal"
        align="center"
        gap={1}
        fullWidth={false}
        className="shrink-0"
      >
        {confirmableCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="border-border-positive-subtle bg-bg-positive-subtle text-text-positive hover:bg-bg-positive-subtle"
            onClick={onConfirmAll}
          >
            <Icon name="Check" size="auto" />
            {t('carePlans.review.confirmAllCount', {
              count: confirmableCount,
            })}
          </Button>
        )}
        {confirmableCount === 0 && allConfirmed && (
          <Tag
            size="s"
            color="positive"
            pill
            subtle
            label={t('carePlans.review.allConfirmed')}
          />
        )}
        {showUndoAll && onUndoAll && (
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={onUndoAll}
            aria-label={t('carePlans.review.undoConfirmAllSection', {
              section: title.toLowerCase(),
            })}
          >
            <Icon name="Undo2" size="auto" />
            {t('carePlans.review.undo')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
